<template>
  <div>
    <div
      class="sectionTitle package"
      data-automation="package-title"
    >
      {{ selectedPackage.name }} <span
        class="tagline"
        data-automation="package-version"
      >{{ info.version }}</span>
    </div>
    <div
      v-if="info.description"
      class="menu showOnMobile"
    >
      <div class="menuItems">
        <a
          data-automation="package-overview-tab"
          class="menuItem"
          :class="{ active: currentTab === 'overview' }"
          @click="setCurrentTab('overview')"
        >Overview</a>
        <a
          data-automation="package-description-tab"
          class="menuItem"
          :class="{ active: currentTab === 'description' }"
          @click="setCurrentTab('description')"
        >Description</a>
      </div>
    </div>

    <div
      v-if="currentTab === 'description'"
      class="description"
      data-automation="package-description"
    >
      <pre v-if="description.raw">{{ description.raw }}</pre>
      <!-- eslint-disable vue/no-v-html -->
      <!-- We sanitize this below, so it should be safe with v-html -->
      <div
        v-if="description.html"
        v-html="description.html"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>

    <div v-if="currentTab === 'overview'">
      <InfoBlockText
        label="Summary"
        data-automation="package-summary"
        :value="info.summary"
      />

      <div class="infoBlock">
        <div class="textWithButton">
          <div
            class="label"
            data-automation="package-install-code"
          >
            Install Code
          </div>
          <button
            class="copyButton"
            data-clipboard-target="#installPythonPackage"
          >
            Copy
          </button>
        </div>
        <pre><code
          id="installPythonPackage"
          data-automation="package-install-python-package"
        >pip install {{ selectedPackage.name }}</code></pre>
      </div>

      <div v-if="Array.isArray(requiresPython)">
        <InfoBlockPackageList
          label="Requires Python"
          data-automation="package-requires"
          :value="requiresPython"
        />
      </div>
      <div v-else>
        <InfoBlockText
          label="Requires"
          data-automation="package-requires"
          :value="requiresPython"
        />
      </div>

      <InfoBlockPackageList
        label="Requires Dist"
        data-automation="package-requires-dist"
        :value="requiresDist"
      />

      <InfoBlockPackageList
        label="Extra"
        data-automation="package-extra"
        :value="requiresExtra"
      />

      <InfoBlockText
        label="Version"
        data-automation="package-version"
        :value="selectedPackage.version"
      />

      <InfoBlockText
        label="Author"
        data-automation="package-author"
        :value="info.author"
      />
      <InfoBlockText
        label="Author Email"
        data-automation="package-author-email"
        :value="info.author_email"
      />

      <InfoBlockText
        label="License"
        data-automation="package-license"
        :value="info.license"
      />

      <ClassifiersList
        label="Classifiers"
        data-automation="package-classifiers"
        :classifiers="info.classifiers"
      />

      <div
        v-if="releases"
        class="infoBlock"
      >
        <div
          class="label"
          data-automation="package-release-versions"
        >
          Available Releases
        </div>
        <div class="flex">
          <div>
            <ReleasesList
              :releases="releases"
              :show-more-button="showMoreReleases"
              :get-next="getNext"
            />
          </div>
          <div
            v-if="selectedPackageReleaseDistributions"
            class="releaseItemInfo"
            data-automation="package-release-item-info"
            :style="{marginTop: (selectedPackageReleaseIndex * 61) + 1 + 'px', flexGrow: 1}"
          >
            <div class="infoBlock">
              <div
                data-automation="install-code-label-copy"
                class="textWithButton"
              >
                <div class="label">
                  Install Code
                </div>
                <button
                  class="copyButton"
                  data-clipboard-target="#installVersion"
                >
                  Copy
                </button>
              </div>
              <pre data-automation="release-install-code"><code id="installVersion">pip install {{ selectedPackage.name }}=={{ selectedPackageVersion }}</code></pre>
              <DistributionList
                label="Distributions"
                data-automation="pypi-distribution-list"
                :value="selectedPackageReleaseDistributions"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapState, mapGetters, mapActions } from 'vuex'
import marked from 'marked'
import sanitizeHtml from 'sanitize-html'
import ClassifiersList from './ClassifiersList'
import ReleasesList from './ReleasesList'
import DistributionList from './DistributionList'
import InfoBlockText from '../InfoBlockText'
import InfoBlockPackageList from '../InfoBlockPackageList'
import { rewriteGithubBlobImageURIs, uriToAbs } from '../../utils/path-utils'

const extraText = 'extra =='

export default {
  name: 'Package',
  components: {
    InfoBlockText,
    InfoBlockPackageList,
    ClassifiersList,
    ReleasesList,
    DistributionList,
  },
  data: function () {
    return {
      currentTab: 'overview',
    }
  },
  computed: {
    ...mapState({
      selectedPackage: state => ({
        ...state.packages.selectedPackage,
      }),
      selectedPackageReleaseDistributions: state => state.packages.selectedPackageReleaseDistributions,
      selectedPackageVersion: state => state.packages.selectedPackageVersion,
      releasesReceived: state => state.releases.releases,
      releasesOrder: state => state.releases.releasesOrder,
      totalReleases: state => state.releases.totalCount,
    }),
    ...mapGetters([
      'getDocument',
    ]),
    selectedPackageReleaseIndex: function () {
      if (this.releases && this.selectedPackageVersion) {
        return Object.entries(this.releases).findIndex((item) => this.selectedPackageVersion === item[0])
      }
      return 0
    },
    requiresPython: function () {
      if (this.info && this.info.requires_python) {
        if (this.info.requires_python.split(',').length > 1) {
          return this.info.requires_python.split(',').map((item) => {
            return { name: item }
          })
        }
        return `Python ${this.info.requires_python}`
      }

      return ''
    },
    // requiresDist filters out "extra" dependencies and formats each into an array of objects
    requiresDist: function () {
      if (this.info && this.info.requires_dist) {
        const requiresDist = this.info.requires_dist
        // 1. Unique filter, 2. Remove "extra" pkgs filter, 3. Format for the UI element correctly
        return requiresDist
          .filter((item, i) => (requiresDist.indexOf(item) === i))
          .filter((item) => !item.includes(extraText))
          .map((item) => {
            let operator, version
            let spl = item.split(' ')
            const pkgName = spl[0]
            // Pull out the operator and version if available. This makes it possible
            // to generate the URL for the pkg
            if (spl.length > 1) {
              spl = spl[1].replace('(', '').replace(')', '').trim()
              operator = spl.slice(0, 2)
              version = spl.slice(2)
            }
            return { name: pkgName, linked_to: true, operator, version }
          })
      }
      return []
    },
    // requiresExtra filters out non-extra dependencies from the requires_dist object and returns
    // an array of objects with just the package names. These look like: bcrypt ; extra == 'bcrypt'
    requiresExtra: function () {
      if (this.info && this.info.requires_dist) {
        const requiresDist = this.info.requires_dist
        // 1. Unique filter, 2. Keep only "extra" pkgs filter, 3. Format for the UI element correctly
        return requiresDist
          .filter((item, i) => requiresDist.indexOf(item) === i)
          .filter((item) => item.includes(extraText))
          .map((item) => {
            const packageNameVersion = item.split(';')[0].trim()
            const extra = item.split(';')[1].trim()

            let operator, version
            let spl = packageNameVersion.split(' ')
            let pkgName = spl[0]
            if (spl.length > 1) {
              spl = spl[1].replace('(', '').replace(')', '').trim()
              operator = spl.slice(0, 2)
              version = spl.slice(2)
            }

            pkgName = `${pkgName}; ${extra}`
            return { name: pkgName, linked_to: true, operator, version }
          })
      }
      return []
    },
    info: function () {
      if (this.selectedPackage && this.selectedPackage.info) {
        return this.selectedPackage.info
      }

      return {}
    },
    // Only display releases with >= 1 available distribution
    releases: function () {
      if (this.selectedPackage && this.releasesReceived && this.releasesOrder) {
        return this.releasesOrder
          .filter(key => this.releasesReceived[key].length > 0)
          .reduce((obj, key) => {
            obj[key] = this.releasesReceived[key]
            return obj
          }, {})
      }
      return {}
    },
    showMoreReleases: function () {
      // Only show the "List more releases" button when the number of releases received is
      // less than the total number of releases
      return Object.keys(this.releasesReceived).length < this.totalReleases
    },
    description: function () {
      // For now only render descriptions for packages with content-type text/markdown, in the future
      // we'll want to preprocess all descriptions to markdown.
      if (this.info.description && this.info.description_content_type === 'text/markdown') {
        const rawHTML = marked(
          this.info.description,
          {
            sanitize: false,
            gfm: true,
            headerIds: false,
          },
        )
        return {
          html: sanitizeHtml(rawHTML, {
            allowedTags: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'p', 'a', 'ul', 'ol',
              'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'br',
              'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'pre', 'img'],
            allowedAttributes: {
              a: ['href', 'name', 'target'],
              img: ['src', 'align', 'style', 'alt', 'height', 'width'],
            },
            transformTags: {
              a: uriToAbs('href', this.selectedPackage.assetsPath),
              img: rewriteGithubBlobImageURIs(uriToAbs('src', this.selectedPackage.assetsPath)),
            },
          }),
        }
      } else if (this.info.description) {
        return { raw: this.info.description.trim() }
      }
      // Return an empty, valid object to avoid UI errors.
      return { html: '', raw: '' }
    },
  },
  watch: {
    selectedPackage: function (newValue, oldValue) {
      if (newValue.id !== oldValue.id) {
        this.setCurrentTab('overview')
        this.getFirst()
      }
    },
  },
  created: function () {
    const main = this.getDocument.getElementById('main')
    main.scrollTop = 0
    main.scrollLeft = 0
    this.getFirst()
  },
  methods: {
    setCurrentTab: function (tab) {
      this.currentTab = tab
    },
    ...mapActions([
      'getFirstReleases',
      'getNextReleases',
    ]),
    getFirst: function () {
      this.getFirstReleases({ sourceId: this.selectedPackage.source_id, packageName: this.selectedPackage.name })
    },
    getNext: function () {
      this.getNextReleases({ sourceId: this.selectedPackage.source_id, packageName: this.selectedPackage.name })
    },
  },
}

</script>
